import { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { Loader } from '@/components/loader/loader';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { Header } from '@/ui/typography/widgets';

import { useCanEditCompany } from '../../hooks/use-can-edit-company';
import { companyIdState, CompanyRatingType } from '../company.state';
import { WIDGET_EDITOR_NOTE_RANKING_HEIGHT } from '../utils/constants';

import { CompanyRatingCard } from './company-rating-card';
import { RankingStarIcon } from './ranking-star-icon';
import { useCompanyRankings } from './use-company-rating.query';
import { useAddCompanyRanking } from './use-company-rating.mutation';
import { useCompanyRankingLoadingStates } from './use-company-rating-loading-states';
import { useAtomValue } from 'jotai';

export const CompanyRatingWidget = ({ className }: { className?: string }) => {
  const { t } = useTranslation('companies');
  const canEditCompany = useCanEditCompany();
  const { data: ratings } = useCompanyRankings();

  const companyId = useAtomValue(companyIdState);
  const disabled = !canEditCompany;
  const { isLoading } = useCompanyRankingLoadingStates(companyId);

  const { addRating, removeRating } = useAddCompanyRanking(companyId);
  const overallAverage = ratings?.overallRating || null;

  const technologyUserRating = ratings?.technology.userRating || null;
  const technologyRating = ratings?.technology.averageRating || null;

  const teamRating = ratings?.team.averageRating || null;
  const teamUserRating = ratings?.team.userRating || null;

  const financialRating = ratings?.financial.averageRating || null;
  const financiaUserRating = ratings?.financial.userRating || null;

  const checkIfRankValueExists = useCallback(
    (rating: number, type: CompanyRatingType) => {
      const ratingWithDecimal = Number(rating.toFixed(1));
      switch (type) {
        case CompanyRatingType.Technology:
          return ratingWithDecimal === technologyUserRating;
        case CompanyRatingType.Team:
          return ratingWithDecimal === teamUserRating;
        case CompanyRatingType.Financial:
          return ratingWithDecimal === financiaUserRating;
      }
    },
    [financiaUserRating, teamUserRating, technologyUserRating],
  );

  const handleRatingChange = (rating: number, type: CompanyRatingType) => {
    if (!companyId) return;

    const shouldDelete = checkIfRankValueExists(rating, type);

    if (shouldDelete) {
      return removeRating.mutate({
        organizationId: companyId,
        type,
      });
    } else {
      return addRating.mutate({
        organizationId: companyId,
        rating: rating,
        type,
      });
    }
  };

  const shouldHideAverages =
    !technologyRating && !teamRating && !financialRating;

  return (
    <RankingContainer name="company-ranking-widget" className={className}>
      <Stack gap="16px" fullWidth>
        <Flexbox name="company-ranking-header" gap="16px" alignItems="center">
          <Header>{t`ranking.header`}</Header>
          <Flexbox
            name="company-ranking-rating-overview"
            gap="10px"
            alignItems="center"
          >
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <Flexbox
                  name="company-ranking-rating-overview-overall"
                  gap="4px"
                >
                  <RatingText data-testid="general-impression">
                    {overallAverage
                      ? overallAverage.toFixed(1)
                      : t`ranking.emptyRankingMessage`}
                  </RatingText>
                  <RankingStarIcon filled />
                </Flexbox>
                {overallAverage && (
                  <GeneralImpression>{t`ranking.generalImpression`}</GeneralImpression>
                )}
              </>
            )}
          </Flexbox>
        </Flexbox>
        <RatingCards name="rating-types" gap="12px" direction="column">
          <CompanyRatingCard
            disabled={disabled}
            hideRankingAverage={shouldHideAverages}
            initialValue={technologyUserRating}
            loading={isLoading}
            onChange={rate =>
              handleRatingChange(rate, CompanyRatingType.Technology)
            }
            rating={technologyRating}
            type={CompanyRatingType.Technology}
          />
          <CompanyRatingCard
            disabled={disabled}
            hideRankingAverage={shouldHideAverages}
            initialValue={teamUserRating}
            loading={isLoading}
            onChange={rate => handleRatingChange(rate, CompanyRatingType.Team)}
            rating={teamRating}
            type={CompanyRatingType.Team}
          />
          <CompanyRatingCard
            disabled={disabled}
            hideRankingAverage={shouldHideAverages}
            initialValue={financiaUserRating}
            loading={isLoading}
            onChange={rate =>
              handleRatingChange(rate, CompanyRatingType.Financial)
            }
            rating={financialRating}
            type={CompanyRatingType.Financial}
          />
        </RatingCards>
      </Stack>
    </RankingContainer>
  );
};

const RankingContainer = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-radius: 20px;
  box-sizing: border-box;
  height: ${WIDGET_EDITOR_NOTE_RANKING_HEIGHT}px;
  max-height: ${WIDGET_EDITOR_NOTE_RANKING_HEIGHT}px;
  overflow: hidden;
  padding: 24px;
  position: relative;
  width: 100%;
`;

const RatingCards = styled(Flexbox)`
  min-height: 0;
`;

const RatingText = styled('p', forwardRef)`
  ${({ theme }) => theme.typography.companyRanking.generalImpressionRating};
`;

const GeneralImpression = styled('p', forwardRef)`
  ${({ theme }) => theme.typography.companyRanking.generalImpressionText};
`;
